import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Offcanvas from "react-bootstrap/Offcanvas";
import InfiniteScroll from "react-infinite-scroll-component";
import Select from "react-select";
import moment from "moment";
import { toast } from "react-toastify";
import LoaderSpinner from "../../common/Loader";
import PnLStatusChip from "./PnLStatusChip";
import PnLCloseReturns, { canCloseReturns } from "./PnLCloseReturns";
import { getPnlRowDetailSellersService, getPnlRowDetailService } from "../../../services/pnlReport.service";
import {
  DETAIL_PAGE_SIZE,
  buildEntityFilter,
  formatMoney,
  getAdjustmentLines,
  getCategoryLabel,
  getLockText,
  getStatusInfo,
  loadEntityList,
  monthLabel,
} from "./pnlReportUtils";

const DRAWER_SCROLL_ID = "pnl-drawer-scroll";
// Change log and transactions load in parallel; one toast covers both failing.
const DETAIL_ERROR_TOAST = { toastId: "pnl-detail-error" };

// SP_PnL_GetRowDetail writes task lines as 'Task #<id> - <title>'; SourceId there is the fund transaction.
const TASK_REFERENCE = /^Task #(\d+)/;

const LineReference = ({ reference }) => {
  const taskId = TASK_REFERENCE.exec(reference ?? "")?.[1];
  if (!taskId) return reference ?? null;
  // New tab so the report filters and open drawer survive.
  return (
    <Link to={`/tasks/${taskId}`} target="_blank" rel="noopener noreferrer">
      {reference}
    </Link>
  );
};

LineReference.propTypes = { reference: PropTypes.string };

const formatPostedDate = (value) => {
  const parsed = moment.utc(value);
  return value && parsed.isValid() ? parsed.format("DD MMM YYYY") : "";
};

const buildDetailRequest = (row, filters) => ({
  year: row.year,
  month: row.month,
  category: row.category,
  subType: row.subType,
  companySellerId: row.companySellerId ?? null,
  ...buildEntityFilter(filters),
});

const ChangeLog = ({ lines }) => {
  const adjustments = getAdjustmentLines(lines);
  return (
    <section className="pnl-detail-section">
      <h3>Change log</h3>
      {adjustments.length === 0 && <p className="pnl-detail-empty">No adjustments posted for this row.</p>}
      {adjustments.map((line) => (
        <div className="pnl-log-item" key={`${line.sourceTable}-${line.sourceId}`}>
          <span className={`pnl-log-amount ${line.adjustment < 0 ? "pnl-neg" : "pnl-pos"}`}>
            {line.adjustment > 0 ? "+" : ""}{formatMoney(line.adjustment)}
          </span>
          {line.reference}
          {line.description && <small>{line.description}</small>}
          <small>Posted on {formatPostedDate(line.postedDate)}</small>
        </div>
      ))}
    </section>
  );
};

ChangeLog.propTypes = { lines: PropTypes.array.isRequired };

const TransactionLines = ({ lines, total, loading, onLoadMore, sellerFilter }) => (
  <section className="pnl-detail-section">
    <h3>Transactions ({total})</h3>
    {sellerFilter}
    {loading && <LoaderSpinner />}
    {!loading && lines.length === 0 && <p className="pnl-detail-empty">No transactions found for this row.</p>}
    {!loading && lines.length > 0 && (
      <InfiniteScroll
        dataLength={lines.length}
        next={onLoadMore}
        hasMore={lines.length < total}
        loader={<p className="pnl-detail-note">Loading more transactions...</p>}
        scrollableTarget={DRAWER_SCROLL_ID}
      >
        <table className="pnl-lines">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Date</th>
              <th className="pnl-num">Gross</th>
              <th className="pnl-num">Cost</th>
              <th className="pnl-num">Net</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={`${line.sourceTable}-${line.sourceId}`}>
                <td>
                  <LineReference reference={line.reference} />
                  {line.companySellerName && <small className="pnl-line-seller">{line.companySellerName}</small>}
                </td>
                <td>{formatPostedDate(line.postedDate)}</td>
                <td className="pnl-num">{formatMoney(line.gross)}</td>
                <td className="pnl-num">{formatMoney(line.cost)}</td>
                <td className="pnl-num">{formatMoney(line.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </InfiniteScroll>
    )}
  </section>
);

TransactionLines.propTypes = {
  lines: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  sellerFilter: PropTypes.node,
};

const SellerFilter = ({ options, values, onChange }) => (
  <div className="pnl-field pnl-detail-filter">
    <label htmlFor="pnl-detail-seller">Seller</label>
    <Select
      inputId="pnl-detail-seller"
      isMulti
      closeMenuOnSelect={false}
      options={options}
      value={options.filter((option) => values.includes(option.value))}
      onChange={(chosen) => onChange(chosen.map((option) => option.value))}
      classNamePrefix="pnl-select"
      placeholder="All sellers"
    />
  </div>
);

SellerFilter.propTypes = {
  options: PropTypes.array.isRequired,
  values: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
};

// A seller-scoped row already belongs to one seller, so it gets no seller list.
const useSellerOptions = (row, filters) => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let ignore = false;
    setOptions([]);
    if (row.companySellerId) return undefined;
    loadEntityList(() => getPnlRowDetailSellersService(buildDetailRequest(row, filters))).then(({ rows, error }) => {
      if (ignore) return;
      if (error) toast.error(error, DETAIL_ERROR_TOAST);
      setOptions(rows.map((seller) => {
        const name = seller.companySellerName || `Seller #${seller.companySellerId}`;
        return { value: seller.companySellerId, label: `${name} (${seller.lineCount})` };
      }));
    });
    return () => {
      ignore = true;
    };
  }, [row, filters]);

  return options;
};

const useChangeLog = (row, filters) => {
  const [changeLog, setChangeLog] = useState({ loading: true, lines: [] });

  useEffect(() => {
    let ignore = false;
    setChangeLog({ loading: true, lines: [] });
    const request = { ...buildDetailRequest(row, filters), adjustmentsOnly: true };
    loadEntityList(() => getPnlRowDetailService(request)).then(({ rows, error }) => {
      if (ignore) return;
      if (error) toast.error(error, DETAIL_ERROR_TOAST);
      setChangeLog({ loading: false, lines: rows });
    });
    return () => {
      ignore = true;
    };
  }, [row, filters]);

  return changeLog;
};

const useTransactionPages = (row, filters, sellerIds) => {
  const [pages, setPages] = useState({ loading: true, lines: [], total: 0 });
  // Bumped on every reload and on unmount so late responses are dropped.
  const generation = useRef(0);

  const fetchPage = useCallback(
    async (pageNumber) => {
      const current = generation.current;
      const request = { ...buildDetailRequest(row, filters), pageNumber, pageSize: DETAIL_PAGE_SIZE };
      if (sellerIds.length) request.companySellerIdList = sellerIds;
      const { rows, total, error } = await loadEntityList(() => getPnlRowDetailService(request));
      if (current !== generation.current) return;
      if (error) toast.error(error, DETAIL_ERROR_TOAST);
      setPages((prev) => {
        const lines = pageNumber === 1 ? rows : [...prev.lines, ...rows];
        // A failed or empty page ends paging so scrolling does not keep retrying it.
        return { loading: false, lines, total: error || rows.length === 0 ? lines.length : total };
      });
    },
    [row, filters, sellerIds]
  );

  useEffect(() => {
    generation.current += 1;
    setPages({ loading: true, lines: [], total: 0 });
    fetchPage(1);
    return () => {
      generation.current += 1;
    };
  }, [fetchPage]);

  const loadMore = () => fetchPage(Math.floor(pages.lines.length / DETAIL_PAGE_SIZE) + 1);

  return { ...pages, loadMore };
};

const NO_FILTERS = {};

const PnLDetailDrawer = ({ row, filters = NO_FILTERS, onClose, onReturnsClosed }) => {
  const changeLog = useChangeLog(row, filters);
  const sellerOptions = useSellerOptions(row, filters);
  const [sellerIds, setSellerIds] = useState([]);
  const transactions = useTransactionPages(row, filters, sellerIds);

  const categoryLabel = getCategoryLabel(row.category);
  const title = categoryLabel === row.subType ? row.subType : `${categoryLabel}: ${row.subType}`;

  return (
    <Offcanvas show placement="end" onHide={onClose} className="pnl-drawer" id={DRAWER_SCROLL_ID}>
      <Offcanvas.Header closeButton>
        <div>
          <Offcanvas.Title as="h2" className="pnl-drawer-title">{title}</Offcanvas.Title>
          <div className="pnl-drawer-sub">{monthLabel(row.year, row.month)}</div>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <PnLStatusChip status={getStatusInfo(row)} />
        <p className="pnl-lock-text">{getLockText(row)}</p>
        <div className="pnl-calc">
          <span>Gross amount</span><span>{formatMoney(row.grossAmount)}</span>
          <span>Cost</span><span>{formatMoney(row.costAmount)}</span>
          <span>Adjustments</span><span>{formatMoney(row.adjustmentAmount)}</span>
          <span className="pnl-calc-total">Net amount</span>
          <span className="pnl-calc-total">{formatMoney(row.netAmount)}</span>
        </div>
        {canCloseReturns(row) && <PnLCloseReturns row={row} onClosed={onReturnsClosed} />}
        {changeLog.loading && <LoaderSpinner />}
        {!changeLog.loading && (
          <>
            <ChangeLog lines={changeLog.lines} />
            <TransactionLines
              lines={transactions.lines}
              total={transactions.total}
              loading={transactions.loading}
              onLoadMore={transactions.loadMore}
              sellerFilter={
                sellerOptions.length > 1 && (
                  <SellerFilter options={sellerOptions} values={sellerIds} onChange={setSellerIds} />
                )
              }
            />
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

PnLDetailDrawer.propTypes = {
  row: PropTypes.shape({
    year: PropTypes.number.isRequired,
    month: PropTypes.number.isRequired,
    category: PropTypes.string.isRequired,
    subType: PropTypes.string,
    statusCode: PropTypes.number,
    grossAmount: PropTypes.number,
    costAmount: PropTypes.number,
    adjustmentAmount: PropTypes.number,
    netAmount: PropTypes.number,
  }).isRequired,
  filters: PropTypes.shape({
    companyIds: PropTypes.arrayOf(PropTypes.number),
    sellerIds: PropTypes.arrayOf(PropTypes.number),
    warehouseIds: PropTypes.arrayOf(PropTypes.number),
  }),
  onClose: PropTypes.func.isRequired,
  onReturnsClosed: PropTypes.func.isRequired,
};

export default PnLDetailDrawer;
